import React, { useState } from 'react';
import { Box, ButtonBase, CircularProgress, Link } from '@mui/material';
import {
  PhotoCamera as PhotoIcon, Science as LabIcon, Medication as SupplementIcon, Chat as ChatIcon,
  Biotech as MatchIcon, VolumeUp as VoiceIcon, BarChart as ChartIcon, Restaurant as FoodIcon,
  CheckCircle as CheckCircleIcon, ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import Txt from '../design-system/Txt';
import { InstrumentCard, CardHead, PanelButton, INK, MONO } from '../instrument';
import type { IAPProduct } from '../../services/iapService';
import type { ProRecap } from './useProPurchase';
import { track } from '../../utils/track';

/**
 * The Pro offer (2026-11-03, Jason: "optimize how we show the keep pro prompt and
 * improve that upgrade page to improve conversions"). Used by the Upgrade page and
 * the one-time offer after the free Pro day, so both say the same thing and both
 * can buy in one tap.
 *
 * Order, top to bottom: what you've already put in (their own numbers, when there
 * are any) → one plan choice with the annual plan picked (per-month price shown)
 * → ONE primary button → what happens during the free week → what Pro adds →
 * restore + the store disclosure, word for word (Apple 3.1.2 / Google Play).
 */

const FEATURES = [
  { icon: PhotoIcon, label: 'Unlimited photo logging', desc: 'Photograph a meal and get the full breakdown' },
  { icon: LabIcon, label: 'Your bloodwork, read', desc: 'Upload a lab PDF or photo. 80+ markers, tracked over time.' },
  { icon: ChatIcon, label: 'Unlimited questions', desc: 'Ask about your food, labs and training, no daily cap' },
  { icon: SupplementIcon, label: 'Supplement label scan', desc: 'Photograph the bottle. Every ingredient, every dose.' },
  { icon: MatchIcon, label: 'Supplements checked against your labs', desc: 'See which supplements line up with your bloodwork' },
  { icon: ChartIcon, label: 'Unlimited analyses', desc: 'Nutrition, bloodwork and correlation reports, no weekly cap' },
  { icon: VoiceIcon, label: 'Spoken coach replies', desc: 'Hear the coach answer out loud' },
  { icon: FoodIcon, label: 'Unlimited food lookups', desc: 'Look up and log any food, no daily ceiling' },
];

// List prices, used until the store returns its own (localized) prices.
const FALLBACK = { monthly: 7.99, yearly: 69.99 };

type Plan = 'yearly' | 'monthly';

const num = (p?: string) => { const n = parseFloat(String(p || '').replace(/[^0-9.]/g, '')); return Number.isFinite(n) ? n : null; };
const money = (n: number, sym = '$') => `${sym}${n.toFixed(2)}`;

export interface ProOfferProps {
  src: string;
  products: IAPProduct[];
  supported: boolean;
  ready: boolean;
  loading: boolean;
  error: string | null;
  success: boolean;
  onPurchase: (productId: string) => void;
  onRestore?: () => void;
  /** 'App Store' | 'Google Play' — who manages the subscription. */
  store: 'App Store' | 'Google Play';
  recap?: ProRecap | null;
  /** Optional lines above the plans (the offer screen's headline). */
  intro?: React.ReactNode;
  /** When purchases can't happen here (web), what the button does instead. */
  onUnsupported?: () => void;
}

const ProOffer: React.FC<ProOfferProps> = ({ src, products, supported, ready, loading, error, success, onPurchase, onRestore, store, recap, intro, onUnsupported }) => {
  const [plan, setPlan] = useState<Plan>('yearly');
  const [allFeatures, setAllFeatures] = useState(false);
  const [showRestore, setShowRestore] = useState(false);

  const yearly = products.find((p) => p.period === 'yearly');
  const monthly = products.find((p) => p.period === 'monthly');
  const sym = (yearly?.price || monthly?.price || '$').trim().match(/^[^\d\s.,]*/)?.[0] || '$';
  const yPrice = num(yearly?.price) ?? FALLBACK.yearly;
  const mPrice = num(monthly?.price) ?? FALLBACK.monthly;
  const save = Math.round((1 - yPrice / (mPrice * 12)) * 100);
  // Store prices are localized strings ("$69.99", "69,99 €"); a bare number gets the symbol.
  const show = (p: string | undefined, n: number) => (p && /[^\d.,\s]/.test(p) ? p : money(n, sym));
  const chosen = plan === 'yearly' ? yearly : monthly;
  const thenText = plan === 'yearly' ? `${show(yearly?.price, yPrice)}/year` : `${show(monthly?.price, mPrice)}/month`;

  const start = () => {
    track('upgrade_cta', { src, plan });
    if (chosen && supported) onPurchase(chosen.id);
    else onUnsupported?.();
  };

  const stats = recap ? [
    recap.meals > 0 && { v: recap.meals, l: recap.meals === 1 ? 'meal logged' : 'meals logged', sub: recap.photoMeals > 0 ? `${recap.photoMeals} by photo` : undefined },
    recap.labMarkers > 0 && { v: recap.labMarkers, l: 'lab markers read' },
    recap.supplements > 0 && { v: recap.supplements, l: recap.supplements === 1 ? 'supplement tracked' : 'supplements tracked' },
    recap.workouts > 0 && { v: recap.workouts, l: recap.workouts === 1 ? 'workout' : 'workouts' },
    recap.questions > 0 && { v: recap.questions, l: recap.questions === 1 ? 'question asked' : 'questions asked' },
  ].filter(Boolean).slice(0, 4) as Array<{ v: number; l: string; sub?: string }> : [];

  const planRow = (key: Plan, title: string, price: string, note: string, tag?: string) => {
    const on = plan === key;
    return (
      <ButtonBase
        role="radio"
        aria-checked={on}
        onClick={() => { setPlan(key); track('plan_select', { src, plan: key }); }}
        sx={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 1.25, p: 1.5, borderRadius: '10px', textAlign: 'left',
          border: `1px solid ${on ? INK.cyan : 'rgba(255,255,255,0.12)'}`,
          background: on ? 'rgba(0,224,255,0.08)' : 'transparent',
          boxShadow: on ? '0 0 16px rgba(0,224,255,0.18)' : 'none',
        }}
      >
        <Box sx={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0, boxSizing: 'border-box', border: `2px solid ${on ? INK.cyan : 'rgba(255,255,255,0.3)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {on && <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: INK.cyan, boxShadow: `0 0 6px ${INK.cyan}` }} />}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <Txt component="span" sx={{ fontFamily: MONO, fontSize: '0.8rem', fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: on ? INK.cyan : '#fff' }}>{title}</Txt>
            {tag && <Txt component="span" sx={{ fontFamily: MONO, fontSize: '0.6rem', fontWeight: 700, letterSpacing: 1, px: 0.75, py: 0.25, borderRadius: '4px', color: INK.green, background: 'rgba(0,230,118,0.14)' }}>{tag}</Txt>}
          </Box>
          <Txt sx={{ fontSize: '0.7rem', color: INK.muted, mt: 0.25 }}>{note}</Txt>
        </Box>
        <Txt component="span" sx={{ fontFamily: MONO, fontSize: '1.25rem', fontWeight: 800, color: '#fff', textShadow: on ? `0 0 14px ${INK.cyan}66` : 'none', whiteSpace: 'nowrap' }}>{price}</Txt>
      </ButtonBase>
    );
  };

  return (
    <Box>
      {intro}

      {stats.length > 0 && (
        <InstrumentCard accent={INK.green} sx={{ mb: 1.25 }}>
          <CardHead label="What you've started" color={INK.green} meta={recap && recap.days > 0 ? `${recap.days} ${recap.days === 1 ? 'day' : 'days'} logged` : undefined} />
          <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(stats.length, 4)}, minmax(0, 1fr))`, gap: 0.75, mt: 0.75 }}>
            {stats.map((s) => (
              <Box key={s.l} sx={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '8px', py: 0.75, px: 0.5, textAlign: 'center', minWidth: 0 }}>
                <Txt sx={{ fontFamily: MONO, fontSize: '1.25rem', fontWeight: 800, color: '#fff', textShadow: `0 0 12px ${INK.green}66` }}>{s.v}</Txt>
                <Txt sx={{ fontSize: '0.6rem', color: INK.muted, lineHeight: 1.3 }}>{s.l}</Txt>
                {s.sub && <Txt sx={{ fontFamily: MONO, fontSize: '0.6rem', color: INK.cyan }}>{s.sub}</Txt>}
              </Box>
            ))}
          </Box>
          <Txt sx={{ fontSize: '0.8rem', color: INK.soft, mt: 1, lineHeight: 1.4 }}>Pro keeps photo logging, your labs read and the coach working on all of it.</Txt>
        </InstrumentCard>
      )}

      {/* One plan choice, annual picked */}
      <Box role="radiogroup" aria-label="Plan" sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1.25 }}>
        {/* The billed amount is the big number (App Store review rejects a per-month figure that
            outshines what is actually charged); the per-month equivalent is the small line. */}
        {planRow('yearly', 'Annual', `${show(yearly?.price, yPrice)}/yr`, `${money(yPrice / 12, sym)}/mo, billed once a year`, save > 0 ? `SAVE ${save}%` : undefined)}
        {planRow('monthly', 'Monthly', `${show(monthly?.price, mPrice)}/mo`, 'Billed every month')}
      </Box>

      {error && (
        <Box sx={{ mb: 1, px: 1.25, py: 1, borderRadius: '8px', border: '1px solid rgba(255,23,68,0.4)', background: 'rgba(255,23,68,0.06)' }}>
          <Txt sx={{ fontSize: '0.8rem', color: '#FF8A9B' }}>{error}</Txt>
        </Box>
      )}
      {success && (
        <Box sx={{ mb: 1, px: 1.25, py: 1, borderRadius: '8px', border: '1px solid rgba(0,230,118,0.4)', background: 'rgba(0,230,118,0.06)' }}>
          <Txt sx={{ fontSize: '0.8rem', color: INK.green }}>You are on Pro. Every feature is on, with no daily limits.</Txt>
        </Box>
      )}

      <PanelButton variant="primary" fullWidth onClick={start} disabled={loading || (!ready && !onUnsupported) || success} sx={{ minHeight: 50, fontSize: '0.9rem' }}>
        {loading ? <CircularProgress size={20} sx={{ color: INK.ground }} /> : (!ready ? 'Loading' : 'Start my free week')}
      </PanelButton>
      <Txt sx={{ textAlign: 'center', fontFamily: MONO, fontSize: '0.7rem', color: INK.soft, mt: 0.75 }}>
        7 days free, then {thenText}. Cancel any time.
      </Txt>
      {ready && !supported && (
        <Txt sx={{ textAlign: 'center', fontSize: '0.7rem', color: INK.cyan, mt: 0.5 }}>Purchases are not available in this browser. Subscribe in the FuelTron app on your phone.</Txt>
      )}

      {/* How the free week works */}
      <InstrumentCard sx={{ mt: 1.5, mb: 1.25 }}>
        <CardHead label="How the free week works" />
        {[
          { when: 'Today', what: 'Every Pro feature is on. No charge.', c: INK.cyan },
          { when: 'Day 7', what: `Your plan starts at ${thenText}, unless you cancel.`, c: INK.amber },
          { when: 'Any time', what: `Cancel in ${store} subscriptions. Cancel before day 7 and you pay nothing.`, c: INK.green },
        ].map((r, i) => (
          <Box key={r.when} sx={{ display: 'grid', gridTemplateColumns: '18px 72px minmax(0, 1fr)', alignItems: 'start', gap: 0.75, py: 0.75, borderTop: i ? `1px solid ${INK.faint}` : 'none' }}>
            <Box sx={{ width: 10, height: 10, mt: 0.4, borderRadius: '50%', background: r.c, boxShadow: `0 0 6px ${r.c}` }} />
            <Txt component="span" sx={{ fontFamily: MONO, fontSize: '0.7rem', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: r.c, mt: 0.1 }}>{r.when}</Txt>
            <Txt component="span" sx={{ fontSize: '0.8rem', color: INK.soft, lineHeight: 1.4 }}>{r.what}</Txt>
          </Box>
        ))}
      </InstrumentCard>

      {/* What Pro adds */}
      <InstrumentCard sx={{ mb: 1.25 }}>
        <CardHead label="What Pro adds" meta={`${FEATURES.length}`} />
        <Box sx={{ mt: 0.5, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 1.5 }}>
          {(allFeatures ? FEATURES : FEATURES.slice(0, 4)).map((f) => {
            const Icon = f.icon;
            return (
              <Box key={f.label} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.75, borderTop: `1px solid ${INK.faint}` }}>
                <Icon sx={{ fontSize: 18, color: INK.cyan, mt: 0.125, filter: `drop-shadow(0 0 4px ${INK.cyan}66)` }} />
                <Box sx={{ minWidth: 0 }}>
                  <Txt sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>{f.label}</Txt>
                  <Txt sx={{ fontSize: '0.7rem', color: INK.muted, lineHeight: 1.35, mt: 0.25 }}>{f.desc}</Txt>
                </Box>
              </Box>
            );
          })}
        </Box>
        {!allFeatures && (
          <ButtonBase onClick={() => setAllFeatures(true)} sx={{ mt: 0.5, minHeight: 36, gap: 0.5, fontFamily: MONO, fontSize: '0.7rem', fontWeight: 700, color: INK.cyan }}>
            {FEATURES.length - 4} more <ExpandMoreIcon sx={{ fontSize: 16 }} />
          </ButtonBase>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75, pt: 0.75, borderTop: `1px solid ${INK.faint}` }}>
          <CheckCircleIcon sx={{ fontSize: 14, color: INK.muted }} />
          <Txt sx={{ fontSize: '0.7rem', color: INK.muted }}>Free keeps typed logging, your scores and 5 questions a day.</Txt>
        </Box>
      </InstrumentCard>

      {/* Restore + store disclosure (Apple 3.1.2c), wording unchanged */}
      <Box sx={{ textAlign: 'center' }}>
        {onRestore && supported && (
          showRestore
            ? <PanelButton onClick={onRestore} disabled={loading} sx={{ mb: 1 }}>Restore purchases</PanelButton>
            : <ButtonBase onClick={() => setShowRestore(true)} sx={{ minHeight: 36, mb: 0.5, fontSize: '0.7rem', color: INK.muted, textDecoration: 'underline' }}>Previously purchased?</ButtonBase>
        )}
        <Txt sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', mb: 1 }}>
          Cancel anytime. Subscription managed through {store === 'App Store' ? 'the App Store' : 'Google Play'}.
        </Txt>
        <Txt sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.7rem', lineHeight: 1.6 }}>
          Free 7-day trial, then payment charged to your {store === 'App Store' ? 'Apple ID' : 'Google Play'} account.
          Subscription auto-renews unless canceled at least 24 hours before the end of the current period.
          No charge during the trial — cancel anytime before it ends.
        </Txt>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1.5 }}>
          <Link href="/terms" sx={{ color: 'rgba(0,224,255,0.7)', fontSize: '0.7rem', fontFamily: MONO, textDecoration: 'underline' }}>Terms of Use</Link>
          <Link href="/privacy" sx={{ color: 'rgba(0,224,255,0.7)', fontSize: '0.7rem', fontFamily: MONO, textDecoration: 'underline' }}>Privacy Policy</Link>
        </Box>
      </Box>
    </Box>
  );
};

export default ProOffer;
